/**
 * Human-readable, one-line promotion summary — shared by the promotions list ("Mechanics" column),
 * the builder's summary rail, and the approval review drawer (plan-011 §8.3-G / §8.4 / §8.7).
 *
 * Takes an i18next `t` (namespace `promotions`) so every word is a translated resource, never a
 * hardcoded literal — this file must stay clean under `npm run check:i18n`.
 */
import type { TFunction } from 'i18next';

import { formatNumber, formatPrice } from './format';
import type { ConditionNode, PromotionAction, PromotionRequest } from '../model/types';

export interface PromoSummaryInput {
  type: PromotionRequest['type'];
  actions: PromotionAction[];
  conditions: ConditionNode[];
  requiresCoupon: boolean;
  channelCodes?: string[] | null;
}

/** Depth-first search for the first leaf of a given type (or one of a set of types). */
function findLeaf(nodes: ConditionNode[], types: string[]): ConditionNode | undefined {
  for (const node of nodes) {
    if (node.nodeType === 'LEAF' && node.type && types.includes(node.type)) return node;
    if (node.children?.length) {
      const found = findLeaf(node.children, types);
      if (found) return found;
    }
  }
  return undefined;
}

function actionFragment(t: TFunction, action: PromotionAction): string {
  const p = action.params ?? {};
  switch (action.type) {
    case 'PERCENT_OFF':
      return t('promotions:summary.percentOff', { percent: formatNumber(p.percent as number) });
    case 'AMOUNT_OFF':
      return t('promotions:summary.amountOff', { amount: formatPrice(p.amount as number) });
    case 'FIXED_PRICE':
      return t('promotions:summary.fixedPrice', { price: formatPrice(p.price as number) });
    case 'FREE_SHIPPING':
      return t('promotions:summary.freeShipping');
    case 'SHIPPING_PERCENT_OFF':
      return t('promotions:summary.shippingPercentOff', {
        percent: formatNumber(p.percent as number),
      });
    default:
      return t(`promotions:rewardType.${action.type}`, { defaultValue: action.type });
  }
}

/**
 * Resolve a product set's display name for the condition fragment. Callers pass a small lookup
 * built from whatever product sets they already fetched (e.g. the builder's `useProductSets()`
 * result) — this function never shows a raw UUID to the merchant.
 */
export interface BuildPromoSummaryOptions {
  resolveProductSetName?: (productSetId: string) => string | undefined;
}

export function buildPromotionSummary(
  t: TFunction,
  promotion: PromoSummaryInput,
  options: BuildPromoSummaryOptions = {},
): string {
  const parts: string[] = [];

  if (promotion.actions.length === 0) {
    parts.push(t('promotions:summary.noActions'));
  } else {
    parts.push(actionFragment(t, promotion.actions[0]));
    if (promotion.actions.length > 1) {
      parts.push(t('promotions:summary.moreActions', { count: promotion.actions.length - 1 }));
    }
  }

  const setLeaf = findLeaf(promotion.conditions, ['PRODUCT_IN_SET', 'CATEGORY_IN_SET']);
  if (setLeaf?.params?.productSetId) {
    const setId = String(setLeaf.params.productSetId);
    const name = options.resolveProductSetName?.(setId) ?? t('promotions:summary.unnamedSet');
    parts.push(t('promotions:summary.onSet', { name }));
  } else {
    const subtotalLeaf = findLeaf(promotion.conditions, ['MIN_SUBTOTAL']);
    if (subtotalLeaf?.params?.amount != null) {
      parts.push(
        t('promotions:summary.minSubtotal', {
          amount: formatPrice(subtotalLeaf.params.amount as number),
        }),
      );
    }
  }

  if (promotion.requiresCoupon) {
    parts.push(t('promotions:summary.requiresCoupon'));
  }

  if (promotion.channelCodes && promotion.channelCodes.length > 0) {
    parts.push(t('promotions:summary.channels', { channels: promotion.channelCodes.join(', ') }));
  }

  return parts.join(' · ');
}
